import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import type { UserProfile } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle, KeyRound, Eye, EyeOff, FileText, CalendarDays, Plus } from "lucide-react";
import { PreencherTesteButton } from "@/components/PreencherTesteButton";
import { gerarArtFake } from "@/lib/mock-test-data";

// ─── Certificado Digital RT ────────────────────────────────────────────────────

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function CertificadoDigitalCard({
  user,
  canManage,
  canView,
}: {
  user: UserProfile;
  canManage: boolean;
  canView: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [senha, setSenha] = useState("");
  const [senhaConfirm, setSenhaConfirm] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: certStatus, refetch: refetchCert, isLoading: certLoading, isError: certError } = useQuery({
    queryKey: [`rt-certificado-${user.id}`],
    queryFn: async () => {
      const res = await fetch(`/api/admin/rt-certificado/${user.id}/status`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Não foi possível consultar o certificado.");
      return res.json() as Promise<{ temCertificado: boolean; senhaConfigurada: boolean; nomeArquivo?: string; criadoEm?: string }>;
    },
    enabled: canView && !!session,
  });

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pfx|p12)$/i.test(file.name) || !file.size || file.size > 5 * 1024 * 1024) {
      toast({ title: "Selecione um certificado .pfx ou .p12 de até 5 MB.", variant: "destructive" });
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
    setSenha("");
    setSenhaConfirm("");
    setShowModal(true);
    e.target.value = "";
  }

  async function handleSubmit() {
    if (!selectedFile || !senha) return;
    if (senha !== senhaConfirm) {
      toast({ title: "As senhas não conferem.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const buffer = await selectedFile.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);
      const res = await fetch(`/api/admin/rt-certificado/${user.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ certificadoBase64: base64, nomeArquivo: selectedFile.name, senha }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar certificado");
      }
      setShowModal(false);
      setSenha("");
      setSenhaConfirm("");
      setSelectedFile(null);
      refetchCert();
      toast({ title: "Certificado digital cadastrado com sucesso." });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Remover o certificado digital de ${user.nome}? Esta ação não pode ser desfeita.`)) return;
    setRemoving(true);
    try {
      const res = await fetch(`/api/admin/rt-certificado/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }
      refetchCert();
      toast({ title: "Certificado removido." });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally { setRemoving(false); }
  }

  function closeModal() {
    if (submitting) return;
    setShowModal(false);
    setSenha("");
    setSenhaConfirm("");
    setSelectedFile(null);
  }

  if (!canView) return null;

  return (
    <div className="mt-2 pt-2 border-t">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs">
          <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-500">Certificado Digital</span>
          {certError ? <span role="alert" className="text-red-700">Falha na consulta <button onClick={() => void refetchCert()}>Tentar novamente</button></span> : certLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
          ) : certStatus?.temCertificado ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <span className="text-green-600 font-medium">Associado</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-amber-600">Não configurado</span>
            </>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-1">
            {certStatus?.temCertificado && (
              <Button
                size="sm"
                variant="ghost"
                className="h-10 text-sm px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                disabled={removing || submitting}
                onClick={handleDelete}
              >
                Remover
              </Button>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pfx,.p12"
                disabled={removing || submitting}
                aria-label="Selecionar certificado digital"
                className="hidden"
                onChange={handleFileSelected}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-10 text-sm shrink-0"
                disabled={removing || submitting}
                onClick={() => fileInputRef.current?.click()}
              >
                Selecionar certificado (.pfx/.p12)
              </Button>
              <span
                className="max-w-40 truncate text-xs text-slate-500"
                title={selectedFile?.name ?? "Nenhum arquivo selecionado"}
              >
                {selectedFile?.name ?? "Nenhum arquivo selecionado"}
              </span>
            </div>
          </div>
        )}
      </div>

      {certStatus?.temCertificado && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
          <KeyRound className="w-3 h-3" />
          <span>Senha cadastrada</span>
          <CheckCircle2 className="w-3 h-3 text-green-500" />
          {certStatus.nomeArquivo && (
            <span className="text-slate-400">• {certStatus.nomeArquivo}</span>
          )}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Senha do Certificado Digital
            </DialogTitle>
            <DialogDescription>
              Arquivo: <strong>{selectedFile?.name}</strong>. A senha será armazenada de forma
              criptografada e não poderá ser visualizada por nenhum administrador ou proprietário.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cert-senha">Senha do certificado</Label>
              <div className="relative">
                <Input
                  id="cert-senha"
                  type={showSenha ? "text" : "password"}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite a senha do certificado"
                  className="pr-10"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  aria-label={showSenha ? "Ocultar senha" : "Mostrar senha"}
                  onClick={() => setShowSenha((v) => !v)}
                >
                  {showSenha ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cert-senha-confirm">Confirmar senha</Label>
              <Input
                id="cert-senha-confirm"
                type={showSenha ? "text" : "password"}
                value={senhaConfirm}
                onChange={(e) => setSenhaConfirm(e.target.value)}
                placeholder="Confirme a senha"
                autoComplete="off"
              />
              {senhaConfirm && senha !== senhaConfirm && (
                <p className="text-xs text-red-500">As senhas não conferem.</p>
              )}
            </div>

            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
              <span>
                Esta senha é <strong>altamente confidencial</strong>. Será criptografada e nenhum
                usuário — incluindo o proprietário da plataforma — terá acesso a ela. Guarde-a em
                local seguro.
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!senha || senha !== senhaConfirm || submitting}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar e Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── RT ART Card ──────────────────────────────────────────────────────────────

type RtArt = {
  id: string;
  rtId: string;
  numeroArt: string;
  descricao?: string | null;
  dataInicio: string;
  dataFim?: string | null;
  status: string;
  criadoEm: string;
};

export function RTArtCard({
  user,
  canManage,
  isTestMode = false,
}: {
  user: UserProfile;
  canManage: boolean;
  isTestMode?: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingArt, setEditingArt] = useState<RtArt | null>(null);
  const [numeroArt, setNumeroArt] = useState("");
  const [descricao, setDescricao] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: arts, refetch, isLoading, isError } = useQuery<RtArt[]>({
    queryKey: [`rt-arts-${user.id}`],
    queryFn: async () => {
      const res = await fetch(`/api/usuarios/${user.id}/arts`, {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Não foi possível consultar as ARTs.");
      return res.json();
    },
    enabled: !!session,
  });

  const artAtiva = arts?.find(
    (a) => a.status === "ativa" && a.dataInicio <= new Date().toISOString().slice(0, 10) && (!a.dataFim || a.dataFim >= new Date().toISOString().slice(0, 10)),
  );

  async function handleCriarArt(e: React.FormEvent) {
    e.preventDefault();
    if (!numeroArt.trim() || !dataInicio) return;
    if (dataFim && dataFim < dataInicio) {
      toast({ title: "Vencimento não pode ser anterior ao início.", variant: "destructive" }); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(editingArt ? `/api/usuarios/arts/${editingArt.id}` : "/api/usuarios/arts", {
        method: editingArt ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          rtId: user.id,
          numeroArt: numeroArt.trim(),
          descricao: descricao || "",
          dataInicio,
          dataFim: dataFim || null,
          status: "ativa",
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar ART");
      }
      refetch();
      toast({ title: editingArt ? "ART atualizada." : "ART cadastrada com sucesso." });
      setEditingArt(null);
      setShowForm(false);
      setNumeroArt("");
      setDescricao("");
      setDataInicio("");
      setDataFim("");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEncerrar(artId: string) {
    if (!confirm("Encerrar esta ART? Ela não poderá ser alterada ou reaberta.")) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/usuarios/arts/${artId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ status: "encerrada" }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message);
      }
      refetch();
      toast({ title: "ART encerrada." });
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally { setSubmitting(false); }
  }

  return (
    <div className="mt-2 pt-2 border-t">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs">
          <FileText className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-500">ART</span>
          {isLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
          ) : artAtiva ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <span className="text-green-600 font-medium">Ativa</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-amber-600">Sem ART ativa</span>
            </>
          )}
        </div>
        {canManage && (
          <div className="flex items-center gap-1.5">
            {isTestMode && showForm && (
              <PreencherTesteButton
                onFill={() => {
                  setNumeroArt(gerarArtFake());
                  setDataInicio(new Date().toISOString().slice(0, 10));
                }}
              />
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-10 text-sm px-2"
              disabled={submitting}
              onClick={() => { setEditingArt(null); setNumeroArt(""); setDescricao(""); setDataInicio(""); setDataFim(""); setShowForm(v => !v); }}
            >
              <Plus className="w-3 h-3 mr-1" />
              Nova ART
            </Button>
          </div>
        )}
      </div>

      {isError && <p role="alert" className="text-sm text-red-700">Falha ao consultar ARTs. <button onClick={() => void refetch()}>Tentar novamente</button></p>}
      {arts?.filter(a => a.status === "ativa").map(art => <div key={art.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
        <span>ART {art.numeroArt} · {art.dataInicio} {art.dataFim ? `até ${art.dataFim}` : "sem vencimento"}</span>
        {canManage && <Button variant="outline" size="sm" disabled={submitting} onClick={() => { setEditingArt(art); setNumeroArt(art.numeroArt); setDescricao(art.descricao || ""); setDataInicio(art.dataInicio); setDataFim(art.dataFim || ""); setShowForm(true); }}>Editar ART</Button>}
        {canManage && <Button variant="ghost" size="sm" disabled={submitting} onClick={() => void handleEncerrar(art.id)}>Encerrar ART</Button>}
      </div>)}
      {/* Histórico compacto de ARTs encerradas */}
      {arts && arts.filter((a) => a.status === "encerrada").length > 0 && (
        <div className="mt-1 space-y-0.5">
          {arts.filter((a) => a.status === "encerrada").slice(0, 2).map((art) => (
            <div key={art.id} className="flex items-center gap-1.5 text-xs text-slate-400">
              <span className="font-mono">{art.numeroArt}</span>
              <span className="text-slate-300">•</span>
              <span>Encerrada</span>
            </div>
          ))}
        </div>
      )}

      {/* Formulário inline para nova ART */}
      {showForm && canManage && (
        <form onSubmit={handleCriarArt} className="mt-2 p-3 rounded-lg bg-slate-50 border space-y-2">
          <p className="text-sm font-medium text-slate-700">{editingArt ? "Editar ART" : "Nova ART"}</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2 space-y-1">
              <Label className="text-xs" htmlFor={`art-numeroArt-${user.id}`}>Número da ART *</Label>
              <Input
                id={`art-numeroArt-${user.id}`}
                value={numeroArt}
                onChange={(e) => setNumeroArt(e.target.value)}
                placeholder="Ex: 123456789"
                className="h-10 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`art-dataInicio-${user.id}`}>Início *</Label>
              <Input
                type="date"
                id={`art-dataInicio-${user.id}`}
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="h-10 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`art-dataFim-${user.id}`}>Vencimento</Label>
              <Input
                type="date"
                id={`art-dataFim-${user.id}`}
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="h-10 text-sm"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs" htmlFor={`art-descricao-${user.id}`}>Descrição</Label>
              <Input
                id={`art-descricao-${user.id}`}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Opcional"
                className="h-10 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-10 text-sm"
              onClick={() => setShowForm(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="h-10 text-sm"
              disabled={submitting || !numeroArt || !dataInicio}
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Salvar"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

